import { 
  Shield,
  Brain,
  Headphones,
  Users,
  BarChart3,
  Target,
  Zap,
  CheckCircle,
  FileText,
  TrendingUp,
  MessageSquare,
  AlertTriangle,
  Eye,
  Award,
  Clock,
  Activity,
  Network,
  Lock,
  Database,
  Rocket,
  Settings,
  PieChart,
  Workflow,
  Gauge
} from 'lucide-react';
import type { ProductFeatureConfig } from './types';

export const bpoQualityAssuranceSystemConfig: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Brain,
      title: "AI-Powered 100% Interaction Monitoring",
      description: "Eliminate blind spots with comprehensive AI analysis of every customer interaction across voice, chat, email, and social channels - no more 2-5% sampling limitations.",
      metrics: "100% coverage, zero blind spots",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: Headphones,
      title: "Real-Time Speech Analytics & Sentiment Detection",
      description: "Advanced AI instantly analyzes conversations for sentiment, emotion, and compliance risks as they happen, enabling immediate intervention when needed.",
      metrics: "Real-time alerts, 99% accuracy",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: CheckCircle,
      title: "Automated Quality Scoring & Evaluation",
      description: "Configurable AI-powered scorecards combining quantitative metrics (FCR, AHT, CSAT) with qualitative behaviors (empathy, communication, problem-solving).",
      metrics: "6x faster evaluation time",
      color: "from-purple-500 to-pink-600"
    },
    {
      icon: Target,
      title: "Smart Issue Detection & Root Cause Analysis",
      description: "AI algorithms detect quality issues before they escalate, identify underlying causes, and provide actionable insights for resolution.",
      metrics: "77% reduction in repeat issues",
      color: "from-red-500 to-orange-600"
    },
    {
      icon: TrendingUp,
      title: "Predictive Trend Analysis & Forecasting",
      description: "Proactively prevent recurring issues with AI-powered pattern recognition and trend forecasting across all quality metrics.",
      metrics: "Proactive issue prevention",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: Users,
      title: "Agent Coaching & Performance Management",
      description: "Automatic coaching plan generation, real-time performance tracking, and skill gap analysis integrated with training systems.",
      metrics: "30%+ CSAT improvement",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: Shield,
      title: "Automated Compliance Verification",
      description: "Real-time compliance monitoring for GDPR, PCI-DSS, HIPAA, and industry-specific regulations with instant violation alerts.",
      metrics: "100% compliance coverage",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: BarChart3,
      title: "Comprehensive Analytics Dashboard",
      description: "Real-time visibility into quality scores, resolution times, agent performance, and customer satisfaction with customizable KPI tracking.",
      metrics: "Real-time insights",
      color: "from-indigo-500 to-purple-600"
    },
    {
      icon: MessageSquare,
      title: "Conversation Intelligence Engine",
      description: "AI transcription, keyword detection, topic mapping, and script adherence tracking across all communication channels.",
      metrics: "99% transcription accuracy",
      color: "from-rose-500 to-red-600"
    },
    {
      icon: Award,
      title: "Calibration & Consistency Management",
      description: "Host calibration sessions with internal managers and BPO evaluators to ensure fair, transparent, and consistent evaluations globally.",
      metrics: "Global evaluation alignment",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Zap,
      title: "Workflow Automation Engine",
      description: "Configure fully automated, manual, or hybrid QA workflows tailored to your organization with intelligent routing and escalation.",
      metrics: "500+ automation triggers",
      color: "from-yellow-500 to-amber-600"
    },
    {
      icon: Network,
      title: "200+ Enterprise Integrations",
      description: "Seamless connectivity with CCaaS platforms, CRMs, WFM systems, LMS platforms, and ticketing systems for unified operations.",
      metrics: "3-week implementation",
      color: "from-teal-500 to-cyan-600"
    }
  ],
  
  advancedCapabilities: [
    {
      icon: Gauge,
      title: "AI Quality Scanner & Auto-Analysis",
      description: "Automatically scan all calls for quality issues and receive AI-powered improvement suggestions with confidence scoring.",
      metrics: "90% automation accuracy",
      color: "from-emerald-500 to-green-600"
    },
    {
      icon: Activity,
      title: "Talk/Listen Ratio Intelligence",
      description: "Advanced analysis of agent talk time, listen time, and silence detection to optimize conversation effectiveness.",
      metrics: "Optimal ratio tracking",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: AlertTriangle,
      title: "Proactive Risk Identification",
      description: "Identify and resolve potential customer concerns before they escalate into complaints or compliance issues.",
      metrics: "21% call volume reduction",
      color: "from-red-500 to-pink-600"
    },
    {
      icon: Eye,
      title: "Live Monitoring & Intervention",
      description: "Real-time call monitoring with AI-powered alerts enabling supervisors to intervene during critical moments.",
      metrics: "Instant supervisor alerts",
      color: "from-purple-500 to-indigo-600"
    },
    {
      icon: FileText,
      title: "Dynamic Scorecard Builder",
      description: "Build custom evaluation scorecards with weighted metrics, conditional logic, and role-specific assessment criteria.",
      metrics: "Unlimited customization",
      color: "from-orange-500 to-red-600"
    },
    {
      icon: PieChart,
      title: "Customer Journey Analytics",
      description: "Track quality across the entire customer journey with touchpoint analysis and experience optimization insights.",
      metrics: "End-to-end visibility",
      color: "from-cyan-500 to-teal-600"
    },
    {
      icon: Workflow,
      title: "Dispute Resolution Workflow",
      description: "Structured workflows for agent disputes, evaluation reviews, and quality appeal processes with full audit trails.",
      metrics: "Fair evaluation process",
      color: "from-indigo-500 to-violet-600"
    },
    {
      icon: Clock,
      title: "Schedule & Capacity Optimization",
      description: "Align QA activities with workforce schedules, optimize evaluator capacity, and balance workload distribution.",
      metrics: "Optimal resource allocation",
      color: "from-amber-500 to-yellow-600"
    },
    {
      icon: Settings,
      title: "Multi-Tenant Architecture",
      description: "Support multiple clients, locations, and business units with isolated data, custom configurations, and separate reporting.",
      metrics: "Enterprise scalability",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: Database,
      title: "Secure Call Recording Storage",
      description: "PCI-DSS compliant recording storage with encryption, retention policies, and secure access controls.",
      metrics: "Unlimited storage capacity",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: TrendingUp,
      title: "Benchmark & Competitive Analysis",
      description: "Compare performance against industry benchmarks, track competitive metrics, and identify improvement opportunities.",
      metrics: "Industry-leading insights",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Award,
      title: "Gamification & Recognition Engine",
      description: "Drive agent engagement with leaderboards, achievement badges, performance challenges, and recognition programs.",
      metrics: "19% sales uplift",
      color: "from-teal-500 to-green-600"
    }
  ],
  
  securityPerformance: [
    {
      icon: Target,
      title: 'SOC 2 Type II & ISO 27001 Certified',
      description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and industry-specific BPO regulations',
      metrics: 'Complete regulatory compliance',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Lock,
      title: 'End-to-End Encryption (AES-256)',
      description: 'Military-grade encryption for all data at rest and in transit, ensuring complete protection of sensitive customer information',
      metrics: 'Military-grade security',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Rocket,
      title: 'Scalable Cloud Infrastructure',
      description: 'Enterprise-grade cloud platform with auto-scaling to handle millions of interactions and 99.99% uptime SLA',
      metrics: '99.99% uptime guaranteed',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Shield,
      title: 'PCI-DSS Compliant Infrastructure',
      description: 'Full PCI-DSS compliance for handling payment card data during customer interactions with secure redaction capabilities',
      metrics: '100% PCI compliance',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: Eye,
      title: 'Multi-Factor Authentication',
      description: 'Enterprise SSO integration with MFA, biometric authentication, and role-based access controls for all users',
      metrics: 'Zero-trust access control',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Activity,
      title: 'Comprehensive Audit Trails',
      description: 'Complete logging of all user actions, evaluation changes, and system events for compliance and security monitoring',
      metrics: 'Complete audit visibility',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Database,
      title: 'Data Residency & Sovereignty',
      description: 'Choose data storage locations to meet regional compliance requirements with geo-redundant backup and disaster recovery',
      metrics: 'Global data compliance',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Network,
      title: 'GDPR & CCPA Compliance',
      description: 'Built-in privacy controls for data subject requests, consent management, and automated data retention policies',
      metrics: 'Privacy-first design',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: CheckCircle,
      title: 'HIPAA Ready Infrastructure',
      description: 'Healthcare-grade security controls and BAA support for organizations handling protected health information',
      metrics: 'Healthcare compliance ready',
      color: 'from-cyan-500 to-blue-600'
    }
  ]
};
